/**
 * Image generation — Gemini Flash Image primary, fal.ai FLUX available in admin.
 */
import { fmtErr } from "@/lib/utils";

const OPENROUTER_API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY as string | undefined;
const FAL_KEY = import.meta.env.VITE_FAL_KEY as string | undefined;

const IMAGE_GEN_MODEL = "google/gemini-2.5-flash-image";

function isDrinkName(foodName: string, description?: string): boolean {
  const name = foodName.toLowerCase();
  // Must be primarily a beverage item, not food with a drink-related word
  const foodIndicators = /(chicken|steak|burger|sandwich|salad|wings|fries|pasta|pizza|soup|ribs|tacos|shrimp|fish|pork|beef|lamb|nachos|quesadilla|wrap|bowl|platter|appetizer|entree|dessert|cake|pie|brownie|bread|rice|grilled|fried|baked|roasted|pretzel|cheese|dip|mozzarella|onion|ring|nugget|tender|slider|bites|poppers|flatbread|bruschetta|hummus|guacamole|artichoke)/i;
  if (foodIndicators.test(name)) return false;
  const nameMatch = /(champagne|cava|prosecco|wine|beer|lager|ipa|ale|negroni|martini|cocktail|spritz|whiskey|whisky|vodka|gin(?!ger)|rum|tequila|mezcal|sauvignon|chardonnay|pinot|merlot|cabernet|aperol|sangria|rosé|riesling|malbec|syrah|shiraz|juice|lemonade|soda|cola|\bwater\b|coffee|espresso|cappuccino|latte|\btea\b(?!k)|smoothie|milkshake|mocktail|\bmilk\b|by the glass)/.test(name);
  if (nameMatch) return true;
  // If name is ambiguous (e.g. "Fountain"), check description for strong drink signals
  const desc = (description || "").toLowerCase();
  return /(\bsoda\b|\bjuice\b|\bcola\b|\bcoffee\b|\btea\b(?!k)|lemonade|smoothie|milkshake|\bmilk\b|\bbeer\b|\bwine\b|cocktail|espresso|cappuccino|\blatte\b|beverage|\bdrink\b)/.test(desc);
}

function inferDrinkVessel(foodName: string, description?: string): string {
  const text = `${foodName} ${description || ""}`.toLowerCase();
  if (/(champagne|cava|prosecco|sparkling)/.test(text)) return "a champagne flute";
  if (/(wine|sauvignon|chardonnay|pinot|merlot|cabernet|riesling|malbec|syrah|shiraz|rosé|rose|grenache|tempranillo)/.test(text)) return "a wine glass";
  if (/(beer|lager|ipa|ale)/.test(text)) return "a beer glass";
  if (/(negroni|martini|cocktail|spritz|aperol|sangria|mocktail)/.test(text)) return "a cocktail glass";
  if (/(whiskey|whisky|bourbon|rum|tequila|mezcal)/.test(text)) return "a rocks glass";
  if (/(coffee|espresso|cappuccino|latte)/.test(text)) return "a coffee cup";
  if (/(tea)/.test(text)) return "a tea cup";
  if (/(smoothie|milkshake|juice|lemonade|\bmilk\b)/.test(text)) return "a tall glass";
  if (/(soda|cola|water)/.test(text)) return "a glass";
  return "a glass";
}

function inferServingVessel(foodName: string, description?: string): string {
  const text = `${foodName} ${description || ""}`.toLowerCase();
  if (/(soup|ramen|curry|risotto|stew|porridge|bisque|chowder)/.test(text)) return "a ceramic bowl";
  if (/(ice cream|cake|dessert|tiramisu|brownie|panna cotta|sorbet|cheesecake)/.test(text)) return "a dessert plate";
  if (/(gravlax|salmon|tartare|ceviche|carpaccio|crudo|sashimi)/.test(text)) return "a ceramic appetizer plate";
  // Shared / board-style spreads: antipasti, charcuterie, mezze, cheese boards, tapas.
  // Putting this BEFORE the entree match catches "antipasti platter" / "italian antipasti".
  if (/(antipasti|antipasto|charcuterie|mezze|tapas|cheese board|tasting board|sharing|platter|crudités|crudites)/.test(text)) return "a rustic wooden board with multiple small plates";
  if (/(steak|entrecôte|entrecote|ribeye|sirloin|fillet|filet|burger|chicken|duck|lamb|veal|pork)/.test(text)) return "a full ceramic dinner plate";
  if (/(fries|nachos|wings|tenders|nuggets|onion rings|fried)/.test(text)) return "a serving basket lined with paper";
  if (/(panini|sandwich|sub|wrap|reuben|blt|club)/.test(text)) return "a wooden serving board sliced to show the cross-section";
  if (/(pizza|flatbread)/.test(text)) return "a wooden pizza peel or pizza stone";
  return "a ceramic plate";
}

function stripAlcoholFromName(foodName: string): string {
  // Strip alcohol words from dish names for image generation only
  // "Whisky Bacon Burger" → "Bacon Burger"
  // BUT preserve place names: "Bourbon Street Steak" stays as-is
  const placeNames = /bourbon street|rum runner|beer garden|wine country/i;
  if (placeNames.test(foodName)) return foodName;
  const alcoholWords = /\b(whisky|whiskey|bourbon|rum|tequila|mezcal|vodka|gin|beer|wine|champagne|prosecco|cava|cognac|brandy|amaretto|kahlua|baileys|sake|malibu|aperol|campari|vermouth|absinthe|grappa|schnapps|limoncello|sambuca|ouzo|pisco|soju|shochu)\b/gi;
  return foodName.replace(alcoholWords, '').replace(/\s{2,}/g, ' ').trim();
}

function stripRestaurantPrefix(foodName: string): string {
  // Strip common restaurant-name prefixes that confuse image generation
  // e.g. "Pines Schnitzel" → "Schnitzel", "Joe's Burger" → "Burger"
  const knownFoodWords = /\b(schnitzel|burger|pizza|pasta|steak|salad|soup|sandwich|chicken|fish|salmon|tuna|cod|shrimp|lobster|risotto|tartare|carpaccio|ceviche|tacos?|sushi|ramen|curry|noodles?|rice|fries|wings|ribs|chops?|fillet|filet|entrecôte|entrecote|ribeye|sirloin|tenderloin|duck|lamb|veal|pork|beef|halloumi|hummus|falafel|gyoza|dumpling|spring roll|tempura|pad thai|pho|bibimbap|paella|gnocchi|ravioli|lasagna|carbonara|bolognese|tiramisu|brownie|cheesecake|pancake|waffle|omelette|benedict|gravlax|toast|wrap|bowl|platter)\b/i;
  const match = foodName.match(knownFoodWords);
  if (match && match.index && match.index > 0) {
    // Only strip if the prefix is short (likely a restaurant name, not a descriptor)
    const prefix = foodName.slice(0, match.index).trim();
    if (prefix.length <= 15 && !/\b(grilled|roasted|fried|crispy|smoked|braised|seared|steamed|baked|pan|slow|char|herb|garlic|truffle|cream|butter|spicy|sweet|sour|hot|cold|fresh|classic|signature|house|homemade)\b/i.test(prefix)) {
      return foodName.slice(match.index).trim();
    }
  }
  return foodName;
}

/** Category-aware texture/detail cues */
function getTextureCues(foodName: string, description?: string): string {
  const text = `${foodName} ${description || ""}`.toLowerCase();
  if (/(salad|slaw|greens|arugula|kale|spinach)/.test(text)) return "fresh crisp leaves, vibrant colors";
  if (/(steak|ribeye|sirloin|fillet|filet|entrecote)/.test(text)) return "visible sear marks, juicy pink center";
  if (/(burger|smash)/.test(text)) return "melted cheese drip, toasted bun, juicy patty";
  if (/(pizza|flatbread)/.test(text)) return "bubbling cheese, charred crust edges";
  if (/(pasta|spaghetti|penne|rigatoni|linguine|fettuccine)/.test(text)) return "glossy sauce coating, al dente texture";
  if (/(soup|ramen|pho|bisque|chowder)/.test(text)) return "rich broth, gentle wisps of heat";
  if (/(sushi|sashimi|maki|roll)/.test(text)) return "glistening fresh fish, precise cuts";
  if (/(cake|brownie|dessert|tiramisu|cheesecake)/.test(text)) return "rich layers, decadent texture";
  if (/(ice cream|gelato|sorbet)/.test(text)) return "creamy scoops, slight melt at edges";
  if (/(fries|chips|onion rings|fried)/.test(text)) return "golden crispy coating, crunchy texture";
  if (/(sandwich|sub|wrap|panini|reuben|blt|club)/.test(text)) return "layered cross-section, fresh ingredients visible";
  if (/(wings|drumstick|chicken)/.test(text)) return "crispy golden skin, juicy meat";
  if (/(taco|burrito|quesadilla)/.test(text)) return "colorful fillings, fresh toppings";
  if (/(hummus|dip|guacamole)/.test(text)) return "smooth creamy swirl, olive oil drizzle";
  if (/(bread|toast|bruschetta|crostini)/.test(text)) return "golden toasted surface, artisan texture";
  if (/(seafood|shrimp|lobster|crab|fish|salmon)/.test(text)) return "glistening flesh, delicate presentation";
  if (/(pork|pulled|ribs|bbq|barbecue)/.test(text)) return "tender pulled texture, caramelized glaze";
  return "appetizing detail, natural texture";
}

/**
 * Strip leading non-letter tokens like dates or order numbers that confuse
 * the model. e.g. "1752 Ridgemount" → "Ridgemount", "#3 Burger" → "Burger".
 * Only strips if at least one letter-led word remains.
 */
function stripNumericPrefix(foodName: string): string {
  const stripped = foodName.replace(/^[\s\d#.\-:_/\\]+/, "").trim();
  if (stripped.length >= 3 && /[a-z]/i.test(stripped)) return stripped;
  return foodName;
}

/**
 * Common negative-prompt tail. Most providers (Google Gemini Flash Image,
 * fal.ai FLUX) honor inline natural-language "no X" cues better than
 * structured negative_prompt fields, so we append it to the main prompt.
 */
const NEGATIVE_TAIL =
  "No text, no logos, no brand labels, no watermarks, no people, no menus, no plates of unrelated food.";

/** Build the prompt for a dish — exported for admin UI (fallback template) */
export const PROMPT_TEMPLATE = (foodName: string, description?: string) => {
  const cleanName = stripAlcoholFromName(
    stripRestaurantPrefix(stripNumericPrefix(foodName)),
  );

  // ─── Drinks: vessel-led composition ─────────────────────────────────────
  if (isDrinkName(foodName, description)) {
    const vessel = inferDrinkVessel(foodName, description);
    const descClause = description ? `, ${description}` : "";
    return [
      `A single serving of ${cleanName}${descClause}, served in ${vessel}`,
      "on a polished restaurant bar surface, professional beverage photography",
      "soft warm lighting, shallow depth of field, condensation detail",
      NEGATIVE_TAIL,
    ].join(". ");
  }

  // ─── Food: vessel + texture-aware plating ──────────────────────────────
  const vessel = inferServingVessel(foodName, description);
  const textureCues = getTextureCues(foodName, description);
  const descClause = description ? ` (${description})` : "";
  return [
    `Hero shot of one serving of ${cleanName}${descClause}, plated on ${vessel}`,
    "professional food photography, overhead three-quarter angle",
    "soft natural lighting, shallow depth of field, restaurant-quality plating",
    textureCues,
    NEGATIVE_TAIL,
  ].join(". ");
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

/** Generate a smart prompt via the edge function (uses learned rules) */
export async function generateSmartPrompt(
  dishName: string,
  description?: string,
  category?: string
): Promise<string> {
  try {
    const resp = await fetch(`${SUPABASE_URL}/functions/v1/generate-prompt`, {
      method: "POST",
      headers: { Authorization: `Bearer ${ANON_KEY}`, "Content-Type": "application/json" },
      body: JSON.stringify({ dish_name: dishName, description, category }),
    });
    if (resp.ok) {
      const data = await resp.json();
      if (data.prompt) {
        console.log(`[smartPrompt] ${data.source} (${data.rules_used || 0} rules, ${data.examples_used || 0} examples): "${data.prompt}"`);
        return data.prompt;
      }
    }
  } catch (e) {
    console.warn("[smartPrompt] fallback to template:", fmtErr(e));
  }
  return PROMPT_TEMPLATE(dishName, description);
}

/** Save a prompt correction for the learning system */
export async function savePromptCorrection(
  dishName: string,
  originalPrompt: string,
  correctedPrompt: string,
  description?: string,
  category?: string
): Promise<void> {
  try {
    await fetch(`${SUPABASE_URL}/functions/v1/prompt-learning`, {
      method: "POST",
      headers: { Authorization: `Bearer ${ANON_KEY}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "save",
        dish_name: dishName,
        original_prompt: originalPrompt,
        corrected_prompt: correctedPrompt,
        description,
        category,
      }),
    });
  } catch (e) {
    console.warn("[promptLearning] save failed:", fmtErr(e));
  }
}

interface ImageResult {
  imageUrl: string | null;
  error?: string;
}

/**
 * Generate a food image via direct OpenRouter API call.
 * Returns { imageUrl } on success, { imageUrl: null, error } on failure.
 * Times out after 15 seconds.
 */
/** Generate via fal.ai FLUX.1 schnell (~500ms, returns URL) */
async function generateViaFal(prompt: string, signal?: AbortSignal): Promise<string | null> {
  if (!FAL_KEY) return null;
  try {
    const response = await fetch("https://fal.run/fal-ai/flux/schnell", {
      method: "POST",
      headers: {
        Authorization: `Key ${FAL_KEY}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        prompt,
        image_size: "square",
        num_inference_steps: 4,
        num_images: 1,
      }),
      signal,
    });
    if (!response.ok) {
      console.warn(`[fal] HTTP ${response.status}`);
      return null;
    }
    const data = await response.json();
    const url = data.images?.[0]?.url;
    if (!url) return null;
    // Fetch the image and convert to data URL for consistency
    const imgResp = await fetch(url);
    const blob = await imgResp.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn("[fal] Error:", fmtErr(e));
    return null;
  }
}

export async function generateFoodImageDirect(
  foodName: string,
  description?: string
): Promise<ImageResult> {
  // Wave B.1: inline the local PROMPT_TEMPLATE instead of round-tripping
  // through the `generate-prompt` edge function. The edge function pulls
  // admin-curated rules from Supabase and was costing 300–800ms per call
  // (×19 dishes = up to 15s of edge-function overhead competing with iOS
  // WKWebView's 6-slot connection pool). The admin re-generation flow
  // still uses generateSmartPrompt directly when reviewing flagged dishes.
  const prompt = PROMPT_TEMPLATE(foodName, description);

  // Gemini Flash Image via OpenRouter (best food understanding)
  if (!OPENROUTER_API_KEY) {
    return { imageUrl: null, error: "No image generation API configured" };
  }

  // One Gemini attempt: returns a URL/data-URL on success, null if the
  // model replied 200 but with no image payload, or throws on transport
  // errors. Extracted so we can RETRY Gemini before paying the quality
  // cost of the fal.ai fallback.
  const tryGemini = async (signal: AbortSignal): Promise<string | null> => {
    const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${OPENROUTER_API_KEY}`,
        "Content-Type": "application/json",
        "HTTP-Referer": "https://yumz.app",
        "X-Title": "Yumz",
      },
      body: JSON.stringify({
        model: IMAGE_GEN_MODEL,
        messages: [{ role: "user", content: prompt }],
      }),
      signal,
    });
    if (!response.ok) {
      const errText = await response.text().catch(() => "");
      console.warn(`[imageGen] Gemini HTTP ${response.status} for "${foodName}": ${errText.slice(0, 120)}`);
      return null;
    }
    let data: any;
    try { data = await response.json(); } catch { return null; }
    const choice = data.choices?.[0]?.message;
    if (choice?.images && Array.isArray(choice.images)) {
      for (const img of choice.images) {
        if (img.type === "image_url" && img.image_url?.url) return img.image_url.url;
      }
    }
    if (choice?.content && Array.isArray(choice.content)) {
      for (const part of choice.content) {
        if (part.type === "image_url" && part.image_url?.url) return part.image_url.url;
        if (part.inline_data) return `data:${part.inline_data.mime_type};base64,${part.inline_data.data}`;
      }
    }
    if (typeof choice?.content === "string" &&
        (choice.content.startsWith("data:") || choice.content.startsWith("http"))) {
      return choice.content;
    }
    return null;
  };

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 45000);

  try {
    // Wave M3 — Gemini is materially higher quality than fal.ai for food
    // (Robin's QA confirmed fal output is poor and the write-time vision
    // gate flags it as wrong dishes). Gemini returns 200-with-no-image on
    // ~30-50% of FIRST attempts, so we retry Gemini ONCE before falling
    // back. This keeps the large majority of images on Gemini; fal.ai is
    // a true last resort, not a coin-flip second choice.
    let url = await tryGemini(controller.signal);
    if (!url) {
      console.log(`[imageGen] Gemini no-image for "${foodName}" — retrying Gemini once`);
      url = await tryGemini(controller.signal);
    }
    if (url) {
      clearTimeout(timeout);
      return { imageUrl: url };
    }

    // Both Gemini attempts came back empty — last-resort fal.ai.
    if (FAL_KEY) {
      console.log(`[imageGen] Gemini gave no image twice for "${foodName}", trying fal.ai fallback`);
      const falUrl = await generateViaFal(prompt, controller.signal);
      clearTimeout(timeout);
      if (falUrl) return { imageUrl: falUrl };
      console.warn(`[imageGen] fal.ai fallback also failed for "${foodName}"`);
      return { imageUrl: null, error: "No image in response" };
    }
    clearTimeout(timeout);
    return { imageUrl: null, error: "No image in response" };
  } catch (e) {
    clearTimeout(timeout);
    if (e instanceof Error && e.name === "AbortError") {
      return { imageUrl: null, error: "Request timed out" };
    }
    return { imageUrl: null, error: e instanceof Error ? e.message : "Unknown error" };
  }
}

/**
 * Generate a single food image and cache it. Used by DishGrid flag button.
 */
export async function queueSingleImageGeneration(
  dishName: string,
  description?: string,
  restaurantName?: string,
): Promise<string | null> {
  const result = await generateFoodImageDirect(dishName, description);
  if (result.imageUrl) {
    // Cache the new image
    try {
      const { setCachedImage } = await import("@/lib/imageCache");
      await setCachedImage(dishName, result.imageUrl, restaurantName, description);
    } catch { /* ignore cache errors */ }
  }
  return result.imageUrl;
}
